import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, ValueProvider, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { AppComponent } from './app.component';
import { LoginModule } from './login/login.module';
import { AnimOnChangeDirective } from './anim-on-change.directive';
import { BreadcrumbService } from './core/breadcrumb.service';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { routing } from './app.routing';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { RootImportModule } from './root-import.module';
import { environment } from '../environments/environment';
import { RouterOutletStubComponent } from '../../testing/router.stubs';
import { OverlayContainer, FullscreenOverlayContainer, Overlay } from '@angular/cdk/overlay';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, MatButtonToggleDefaultOptions } from '@angular/material/button-toggle';
import { NgxsModule } from '@ngxs/store';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { NgxsErrorHandlerPluginModule } from '@sportlogiq/main/core/ngxs-plugins/error/error-handler-plugin.module';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { HttpMockRequestInterceptor } from './interceptors/HttpMockRequestInterceptor';
import { NoopHttpRequestInterceptor } from './interceptors/NoopHttpRequestInterceptor';
import { ContextMessageComponent } from './shared/context-message/context-message.component';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialogModule } from '@angular/material/dialog';
import { MatNativeDateModule } from '@angular/material/core';
import { UserInitializer } from './core/user.initializer';
import { AppLoaderService } from '@sportlogiq/core/app-loader.service';
import { VersionCheckService } from './core/version-check.service';
import { CanvasOptionsService } from './core/canvasoptions.service';
import { MatIconModule } from '@angular/material/icon';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import {
    MAT_AUTOCOMPLETE_SCROLL_STRATEGY as MAT_MDC_AUTOCOMPLETE_SCROLL_STRATEGY,
    MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
} from '@angular/material/autocomplete';
import { MAT_SELECT_SCROLL_STRATEGY as MAT_MDC_SELECT_SCROLL_STRATEGY, MAT_SELECT_CONFIG } from '@angular/material/select';
import { MAT_TOOLTIP_SCROLL_STRATEGY } from '@angular/material/tooltip';
import { MAT_PAGINATOR_DEFAULT_OPTIONS } from '@angular/material/paginator';
import { PAGINATOR_DEFAULT_PAGE_SIZE_OPTIONS } from 'environments/constants';
import { datadogRum } from '@datadog/browser-rum';

if (environment.production) {
    datadogRum.init({
        applicationId: '2daa4d38-59fa-4572-a844-b341742b2e5b',
        clientToken: 'pub0b1d342890922dba9b0e04e523576a33',
        site: 'datadoghq.com',
        service: 'coaching-ice',
        env: environment.production ? 'production' : 'development',
        version: '1.0.0',
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        trackBfcacheViews: true,
        defaultPrivacyLevel: 'mask-user-input',
    });
}

const WINDOW_PROVIDER: ValueProvider = {
    provide: 'Window',
    useValue: window,
};

export function initUserFactory(userInitializer: UserInitializer) {
    return () => userInitializer.initializeUser();
}

@NgModule({ declarations: [RouterOutletStubComponent, AppComponent, AnimOnChangeDirective, ContextMessageComponent],
    bootstrap: [AppComponent],
    /** Import order for this module is important. It needs to be imported before any NgxsModule.forFeature */
    imports: [
        NgxsModule.forRoot([], {
            developmentMode: !environment.production,
            selectorOptions: {
                suppressErrors: environment.production,
            },
        }),
        NgxsReduxDevtoolsPluginModule.forRoot({ disabled: environment.production }),
        NgxsRouterPluginModule.forRoot(),
        NgxsFormPluginModule.forRoot(),
        NgxsErrorHandlerPluginModule.forRoot(),
        RootImportModule,
        BrowserModule,
        BrowserAnimationsModule,
        routing,
        LoginModule,
        MatSnackBarModule,
        MatDialogModule,
        MatNativeDateModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: exportStaticLoader,
                deps: [HttpClient],
            },
        }),
        MatIconModule
    ],
    providers: [
        BreadcrumbService,
        {
            provide: MAT_MDC_AUTOCOMPLETE_SCROLL_STRATEGY,
            useFactory: (overlay: Overlay) => () => overlay.scrollStrategies.reposition(),
            deps: [Overlay],
        },
        {
            provide: MAT_MDC_SELECT_SCROLL_STRATEGY,
            useFactory: (overlay: Overlay) => () => overlay.scrollStrategies.reposition(),
            deps: [Overlay],
        },
        // MDC selects/autocompletes/button-toggles render a trailing checkmark for the selected
        // option by default, hide it app-wide.
        { provide: MAT_SELECT_CONFIG, useValue: { hideSingleSelectionIndicator: true } },
        { provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS, useValue: { hideSingleSelectionIndicator: true } },
        {
            provide: MAT_TOOLTIP_SCROLL_STRATEGY,
            useFactory: (overlay: Overlay) => () => overlay.scrollStrategies.reposition(),
            deps: [Overlay],
        },
        {
            provide: MAT_PAGINATOR_DEFAULT_OPTIONS,
            useValue: {
                pageSizeOptions: PAGINATOR_DEFAULT_PAGE_SIZE_OPTIONS,
                showFirstLastButtons: true
            }
        },
        {
            provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
            useValue: {
                duration: 5000,
                verticalPosition: 'bottom',
                horizontalPosition: 'center',
                panelClass: ['sl-snackbar__panel']
            }
        },
        AppLoaderService,
        WINDOW_PROVIDER,
        UserInitializer,
        provideAppInitializer(() => {
            const initializerFn = (initUserFactory)(inject(UserInitializer));
            return initializerFn();
        }),
        { provide: OverlayContainer, useClass: FullscreenOverlayContainer },
        {
            provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS,
            useValue: {
                appearance: 'legacy',
                hideSingleSelectionIndicator: true,
                hideMultipleSelectionIndicator: true,
            } as MatButtonToggleDefaultOptions,
        },
        environment.simulateLive
            ? {
                provide: HTTP_INTERCEPTORS,
                useClass: HttpMockRequestInterceptor,
                multi: true,
            }
            : {
                provide: HTTP_INTERCEPTORS,
                useClass: NoopHttpRequestInterceptor,
                multi: true,
            },
        CanvasOptionsService,
        VersionCheckService,
        {
            provide: MAT_TABS_CONFIG,
            useValue: {
                animationDuration: '0'
            }
        },
        provideZoneChangeDetection(),
        provideHttpClient(withXhr(), withInterceptorsFromDi())
    ] })
export class AppModule {}

export function exportStaticLoader(http: HttpClient) {
    return new TranslateHttpLoader(http, '/assets/i18n/', '.json');
}
